import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { getUserWalletDetailsById, withdrawFromWallet } from '../../../actions/wallet';
import { closeModalAction } from '../../../actions/modal';
import { wallet as walletTypes } from '../../../types';
import Form from '../../../common/Form';
import Input from '../../../common/Form/Input';
import { useForm } from 'react-hook-form';
import { convertToLakhOrCrore, isBoltOrg, ScogoID } from '../../../utils/common';
import { convertIntoPaisa, convertIntoRupee, PaymentProvider } from '../../../utils/utils';
import { getUserDetailsByID, postBankDetails } from '../../../actions/users';
import { SkeltonCardOne } from '../../../common/SkeltonCardOne';
import { RenderBankDetails, ScogoAmountBalance } from '../../Wallet/Modals/SendToBank';
import development from '../../../config/env/development';
import config from '../../../config';

const formName = 'userDirectBankTransfer';

export default function UserDirectBankTransfer({ user_id }) {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const defaultValues = { amount: '', otp: '', bank: '', acc_num: '', acc_type: 'SAVINGS', upi_vpa: '', ifsc: '', holder_name: '' };
    const myWalletDetail = useSelector((state) => state.wallet.myWalletDetail);
    const [amountError, setAmountError] = useState('');
    const isShowDailyLimit = false;
    const userDetailsById = useSelector((state) => state.user.userDetailsById);
    const isUserDetailsByIdLoading = useSelector((state) => state.loading.isUserDetailsByIdLoading);
    const userWalletById = useSelector((state) => state.wallet.userWalletById);
    const [showAddBankForm, setShowAddBankForm] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('account_number');
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const memeoizedWalletDetails = useMemo(() => {
        let w = {};
        if (userWalletById[user_id]) {
            w = userWalletById[user_id];
        }
        return w;
    }, [userWalletById, user_id]);

    useEffect(() => {
        dispatch(getUserWalletDetailsById({ userId: user_id }));
    }, [user_id, dispatch]);

    const memoizedUserBankDetails = useMemo(() => {
        let data = {};
        if (userDetailsById[user_id]) {
            data = userDetailsById[user_id]?.user?.bank ? userDetailsById[user_id]?.user?.bank : {};
        }
        return data;
    }, [userDetailsById, user_id]);

    const hasBankDetails = useMemo(() => {
        return memoizedUserBankDetails && Object.keys(memoizedUserBankDetails).length > 0;
    }, [memoizedUserBankDetails]);

    useEffect(() => {
        if (user_id && !userDetailsById[user_id]) {
            dispatch(getUserDetailsByID({ user_id: user_id }));
        }
    }, [user_id, dispatch]);

    useEffect(() => {
        return () => dispatch({ type: walletTypes.WITHDRAW_FROM_WALLET_OTP_SUCCESS, payload: false });
    }, [dispatch]);

    const handleFormSubmit = (formValues) => {
        if (showAddBankForm) {
            dispatch(postBankDetails({
                userId: user_id, bank_details: {
                    bank: formValues.bank,
                    acc_num: formValues.acc_num,
                    upi_vpa: formValues.upi_vpa,
                    ifsc: formValues.ifsc,
                    holder_name: formValues.holder_name
                },
                callUserDetails: true,
                closeModal: false
            }));
            return;
        }

        const amount = parseInt(formValues.amount);

        // Validate amount
        if (amount <= 0) {
            setAmountError('Amount must be greater than 0');
            return;
        }

        if (amount > myWalletDetail?.balance) {
            setAmountError('Amount exceeds available balance');
            return;
        }

        if (isShowDailyLimit && myWalletDetail?.withdraw_limit && amount > myWalletDetail.withdraw_limit) {
            setAmountError(`Amount exceeds daily withdrawal limit of ₹${myWalletDetail.withdraw_limit}`);
            return;
        }
        setAmountError('');
        const payload = {
            beneficiary: user_id,
            amount: convertIntoPaisa(amount),
            currency: "INR",
            withdraw_to: paymentMethod,
            provider: PaymentProvider.RAZORPAY,
            remarks: formValues.remarks
        };

        if (newLoggedUserData.org.id === ScogoID) { // If Logged In User Is Scogo User Then Send Provider BULKPE
            payload.provider = PaymentProvider.BULKPE;
        }

        dispatch(withdrawFromWallet({
            data: payload,
            formName,
            cleanUp: true,
            callUserWalletDetails: true
        }));
    };

    const methods = useForm({ defaultValues, mode: 'all' });
    const { watch } = methods;
    const currentAmount = watch('amount');
    const currentBank = watch('bank');
    const currentAccNum = watch('acc_num');
    const currentIfsc = watch('ifsc');
    const currentHolderName = watch('holder_name');

    useEffect(() => {
        if (development.name === config.name) {
            methods.setValue('bank', 'HDFC Bank');
            methods.setValue('acc_num', '1121431121541121');
            methods.setValue('ifsc', 'HDFC0001234');
            methods.setValue('holder_name', 'John Doe');
            methods.setValue('upi_vpa', 'test@upi');
        }
        if (hasBankDetails) {
            setShowAddBankForm(false);
        }
    }, [methods, hasBankDetails]);

    // For Bolt org users, default the Account Holder Name to the user's display name
    useEffect(() => {
        if (newLoggedUserData?.org?.id === isBoltOrg && userDetailsById[user_id]?.user?.full_name) {
            methods.setValue('holder_name', userDetailsById[user_id].user.full_name);
        }
    }, [newLoggedUserData?.org?.id, userDetailsById, user_id, methods]);

    // For Bolt org users, the Account Holder Name must match the user's display name
    const validateHolderName = (value) => {
        const requiredName = userDetailsById[user_id]?.user?.full_name;
        if (newLoggedUserData?.org?.id === isBoltOrg && requiredName && value !== requiredName) {
            return `Account Holder Name must be ${requiredName}`;
        }
        return true;
    };

    const isSubmitDisabled = hasBankDetails
        ? (!currentAmount || amountError)
        : showAddBankForm
            ? (!currentBank || !currentAccNum || !currentIfsc || !currentHolderName)
            : false;

    if (isUserDetailsByIdLoading) return <SkeltonCardOne />

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full"
        >
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-100 flex items-center gap-4">
                {/* <h2 className="text-lg font-semibold text-gray-800">Withdraw Amount</h2> */}
                <motion.div
                    className="flex flex-wrap gap-2"
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                >
                    <motion.div
                        className="bg-white py-1 text-font11 px-3 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-blue-100"
                        whileHover={{ scale: 1.02 }}
                    >
                        <span className="font-medium text-gray-700">User Wallet Balance:</span>
                        <span className="text-blue-600 font-semibold ml-1">₹ {memeoizedWalletDetails?.balance ? convertToLakhOrCrore(convertIntoRupee(memeoizedWalletDetails.balance)) : 0}</span>
                    </motion.div>
                    <ScogoAmountBalance />
                </motion.div>
            </div>
            <div className='px-4 py-2'>
                <RenderBankDetails bankDetails={memoizedUserBankDetails} />
            </div>
            <Form
                methods={methods}
                onSubmit={handleFormSubmit}
                submitButtonText={showAddBankForm ? 'Add Bank Account' : hasBankDetails ? 'Withdraw' : 'Add Bank Account'}
                onCancel={() => dispatch(closeModalAction())}
                showSubmit
                submitContainerClassName="flex gap-2 justify-end py-3 px-4 bg-gray-50 border-t border-gray-100 sticky bottom-0 z-10"
                isLoading={formButtonLoading[formName]}
                isSubmitDisabled={isSubmitDisabled}
                submitButtonClassName="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md transition-colors duration-200 text-sm"
                cancelButtonClassName="border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-md transition-colors duration-200 text-sm"
            >
                <div className="overflow-y-auto max-h-[calc(100vh-220px)]">
                    <motion.div
                        className="px-4 pb-4 relative"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        {!hasBankDetails && !showAddBankForm ? (
                            <motion.div
                                className="flex flex-col items-center justify-center py-6"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                <span className="material-icons text-blue-500 text-4xl mb-3">account_balance</span>
                                <p className="text-font14 font-medium text-gray-700 mb-4">Bank account details are required for withdrawal</p>
                                <button
                                    type="button"
                                    onClick={() => setShowAddBankForm(true)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors duration-200 text-sm flex items-center"
                                >
                                    <span className="material-icons text-font16 mr-1">add</span>
                                    Add Bank Account
                                </button>
                            </motion.div>
                        ) : showAddBankForm ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Bank Name"
                                    name="bank"
                                    placeholder="Enter bank name"
                                    required
                                    className="w-full"
                                />
                                <Input
                                    label="Account Number"
                                    name="acc_num"
                                    placeholder="Enter account number"
                                    required
                                    className="w-full"
                                />
                                {/* <Input
                                    label="Account Type"
                                    name="acc_type"
                                    placeholder="SAVINGS"
                                    defaultValue="SAVINGS"
                                    className="w-full"
                                    required
                                /> */}
                                <Input
                                    label="UPI VPA (Optional)"
                                    name="upi_vpa"
                                    placeholder="Enter UPI ID"
                                    className="w-full"
                                />
                                <Input
                                    label="IFSC Code"
                                    name="ifsc"
                                    placeholder="Enter IFSC code"
                                    required
                                    className="w-full"
                                />
                                <Input
                                    label="Account Holder Name"
                                    name="holder_name"
                                    placeholder="Enter account holder name"
                                    required
                                    validate={validateHolderName}
                                    className="w-full"
                                />
                            </div>
                        ) : (
                            <div className='flex gap-4'>
                                <div className="mb-6">
                                    <label className="text-gray-800 font-semibold mb-3 block text-sm">Choose Payment Method</label>
                                    <div className="flex gap-6 mt-2">
                                        <label className="flex items-center cursor-pointer bg-white hover:bg-blue-50 transition-colors duration-200 px-4 py-2 rounded-lg border-2 border-blue-100">
                                            <input
                                                onClick={() => setPaymentMethod('account_number')}
                                                type="radio"
                                                name="payment_method"
                                                value="account_number"
                                                defaultChecked
                                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700 flex items-center">
                                                <span className="material-icons text-blue-600 mr-2 text-lg">account_balance</span>
                                                IMPS
                                            </span>
                                        </label>
                                        <label className="flex items-center cursor-pointer bg-white hover:bg-blue-50 transition-colors duration-200 px-4 py-2 rounded-lg border-2 border-blue-100">
                                            <input
                                                onClick={() => setPaymentMethod('upi')}
                                                type="radio"
                                                name="payment_method"
                                                value="upi"
                                                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700 flex items-center">
                                                <span className="material-icons text-blue-600 mr-2 text-lg">smartphone</span>
                                                UPI
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <Input
                                        allowFloats
                                        className="w-full"
                                        label="Amount"
                                        type={"number"}
                                        name="amount"
                                        placeholder={"Enter amount to withdraw"}
                                        required
                                        onChange={() => setAmountError('')}
                                        inputClassName="border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 rounded-md text-sm"
                                        labelClassName="text-gray-700 font-medium mb-1 block text-sm"
                                    />
                                </div>

                                {amountError && (
                                    <motion.p
                                        className="text-red-500 text-font11 -mt-2 mb-3"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                    >
                                        {amountError}
                                    </motion.p>
                                )}

                                <Input
                                    label="Remarks"
                                    name="remarks"
                                    placeholder="Enter remarks"
                                    className="w-full"
                                    required
                                />
                            </div>
                        )}
                        {isShowDailyLimit && hasBankDetails && (
                            <motion.div
                                className="flex items-center text-slate-700 text-font11 mt-2 bg-yellow-50 p-1.5 rounded-md border border-yellow-100"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 }}
                            >
                                <span className="material-icons text-yellow-500 text-font12 mr-1">info</span>
                                <p>Your daily withdrawal limit is <span className="font-semibold">₹ {myWalletDetail?.withdraw_limit}</span></p>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </Form>
        </motion.div>
    );
};
